import { resolveAssetImage } from "@/utils/common"
import styled from "@emotion/styled"
import { computed, signal, Signal } from "@preact/signals"
import AssetKey from "custom/asset_key"
import { useEffect, useRef, useState } from "preact/compat"
const ImageStyled = styled.img``

type ImageProps = ExtractStyledProps<typeof ImageStyled>

type Props = Omit<ImageProps, "src"> & {
    src: AssetKey
    lazy?: boolean
}

// 創建一個 signal map 來緩存已解析的圖片路徑
const imageSignalMap = new Map<string, Signal<string>>()

function getImageSignal(src: string) {
    if (!imageSignalMap.has(src)) {
        const newSignal =  computed(() => src.startsWith("http") ? src : resolveAssetImage(src))
        imageSignalMap.set(src, newSignal)
    }
    return imageSignalMap.get(src)!
}

function LazyImage(props: Omit<Props, "lazy">) {
    const { src: assets_src, ...rest } = props
    const srcSignal = getImageSignal(assets_src as string)
    const imgRef = useRef<HTMLImageElement>(null)
    const [isLoaded, setIsLoaded] = useState(false)

    useEffect(() => {
        const img = new window.Image()
        img.onload = () => {
            if (imgRef.current) {
                imgRef.current.src = srcSignal.value
                setIsLoaded(true)
            }
        }
        img.onerror = () => {
            if (imgRef.current) {
                imgRef.current.src = srcSignal.value
                setIsLoaded(true)
            }
        }
        // 開始載入圖片
        img.src = srcSignal.value
        return () => {
            img.onload = null
            img.onerror = null
        }
    }, [srcSignal])

    return (
        <ImageStyled
            ref={imgRef}
            src={isLoaded ? srcSignal.value : ""}
            style={{
                ...rest.style,
                opacity: isLoaded ? 1 : 0,
                transition: "opacity 0.3s ease",
            }}
            loading="lazy"
            {...rest}
        />
    )
}

export default function Image(props: Props) {
    const { src: assets_src, lazy = true, ...rest } = props
    const srcSignal = getImageSignal(assets_src as string)

    return lazy ? (
        <LazyImage
            src={assets_src}
            {...rest}
        />
    ) : (
        <ImageStyled
            src={srcSignal.value}
            {...rest}
        />
    )
}

export function ImageComponent(src: AssetKey, lazy = true) {
    return function Component(props: Omit<Props, "src">) {
        return (
            <Image
                src={src}
                lazy={lazy}
                {...props}
            />
        )
    }
}
